import {
  Bookmark,
  BookmarkCheck,
  CalendarDays,
  ExternalLink,
  FileText,
  Link2,
} from "lucide-react";
import type { DocumentPreview } from "../lib/documentSearch/types";
import { BrandRibbon } from "./BrandRibbon";

type SourcePreviewPanelProps = {
  preview: DocumentPreview | null;
  pinned: boolean;
  onPinToggle: () => void;
  onOpenSource: () => void;
};

export function SourcePreviewPanel({
  preview,
  pinned,
  onPinToggle,
  onOpenSource,
}: SourcePreviewPanelProps) {
  if (!preview) {
    return null;
  }

  return (
    <aside className="preview-panel" aria-label="Document preview">
      <div className="preview-panel__header">
        <BrandRibbon />
        <div className="preview-panel__header-copy">
          <span className="document-type document-type--light">
            <FileText size={15} aria-hidden="true" />
            {preview.type}
          </span>
          <h2>{preview.title}</h2>
          <p>{preview.summary}</p>
        </div>
      </div>

      <div className="preview-panel__body">
        <div className="preview-panel__rail">
          <div className="preview-actions">
            <button
              className="primary-button primary-button--full"
              type="button"
              onClick={onPinToggle}
            >
              {pinned ? (
                <BookmarkCheck size={18} aria-hidden="true" />
              ) : (
                <Bookmark size={18} aria-hidden="true" />
              )}
              <span>{pinned ? "Pinned source" : "Pin source"}</span>
            </button>
            <a
              className="secondary-button"
              href={preview.sourceUrl}
              onClick={onOpenSource}
              rel="noreferrer"
              target="_blank"
            >
              <ExternalLink size={18} aria-hidden="true" />
              <span>Open source</span>
            </a>
          </div>

          <dl className="metadata-grid">
            <div>
              <dt>
                <CalendarDays size={15} aria-hidden="true" />
                Year
              </dt>
              <dd>{preview.year > 0 ? preview.year : "Not set"}</dd>
            </div>
            <div>
              <dt>Pages</dt>
              <dd>{preview.pages > 0 ? preview.pages : "Source file"}</dd>
            </div>
            <div>
              <dt>Size</dt>
              <dd>{preview.fileSize}</dd>
            </div>
            <div>
              <dt>Source</dt>
              <dd>{preview.department}</dd>
            </div>
          </dl>

          <section className="preview-section preview-section--matches">
            <h3>Matched sections</h3>
            <div className="section-tags">
              {preview.sectionMatches.map((section) => (
                <span key={section}>{section}</span>
              ))}
            </div>
          </section>

          <div className="source-path">
            <Link2 size={15} aria-hidden="true" />
            <span>{preview.fileName}</span>
          </div>
        </div>

        <div className="preview-panel__main">
          <section className="preview-section preview-section--facts">
            <h3>Key facts</h3>
            <ul className="fact-list">
              {preview.keyFacts.map((fact) => (
                <li key={fact}>{fact}</li>
              ))}
            </ul>
          </section>

          <section className="preview-section preview-section--excerpts">
            <h3>Excerpts</h3>
            <div className="excerpt-stack">
              {preview.excerpts.map((excerpt) => (
                <blockquote key={`${excerpt.heading}-${excerpt.page}`}>
                  <strong>{excerpt.heading}</strong>
                  <p>{excerpt.text}</p>
                  <cite>Page {excerpt.page}</cite>
                </blockquote>
              ))}
            </div>
          </section>
        </div>
      </div>
    </aside>
  );
}
